import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useApolloClient } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import {
  useProfileMergeStore,
  actionableItems,
  firstInvalidDateKey,
  firstInvalidFieldKey,
  firstInvalidRowFieldKey,
  firstUnknownLanguageKey,
  firstUnreviewedKey,
  guideProgress,
  guideSteps,
  hasInvalidDates,
  hasInvalidFields,
  hasUnknownLanguages,
  resolutionsFor,
  unreviewedNeighbors,
} from "@/stores/profile-merge-store";
import { commitProfileMerge } from "./profile-merge-commit";

function Chevron({ direction }: { direction: "back" | "next" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === "back" ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
    </svg>
  );
}

// Bottom action bar for an active in-line LinkedIn merge — the same floating
// toast the normal profile edit SaveBar uses, so importing feels like ordinary
// editing. Shows how many changes LinkedIn proposes, how far the guided review
// has come, and the actions: keep everything, take everything, or SAVE the
// current per-item selections. Per-item choices live under each field/section
// (the guided review — see ProfileMergeGuide).
//
// Renders nothing when there is no preview or nothing actionable, so it is safe
// to mount unconditionally on the profile page.
export function ProfileMergeBar({
  personId,
  expertiseBase = 0,
}: {
  // The genesis person id (myself.id) — required to create/update entries.
  personId?: string;
  // How many expertises the user already has, so imported skills sort after them.
  expertiseBase?: number;
}) {
  const intl = useIntl();
  const preview = useProfileMergeStore((s) => s.preview);
  const choices = useProfileMergeStore((s) => s.choices);
  const edits = useProfileMergeStore((s) => s.edits);
  const rowEdits = useProfileMergeStore((s) => s.rowEdits);
  const rowExistingEdits = useProfileMergeStore((s) => s.rowExistingEdits);
  const guideKey = useProfileMergeStore((s) => s.guideKey);
  const setMany = useProfileMergeStore((s) => s.setMany);
  const stepBy = useProfileMergeStore((s) => s.stepBy);
  const dropResolved = useProfileMergeStore((s) => s.dropResolved);
  const revealDateErrorsNow = useProfileMergeStore((s) => s.revealDateErrorsNow);
  const activateStep = useProfileMergeStore((s) => s.activateStep);
  const reset = useProfileMergeStore((s) => s.reset);
  const apollo = useApolloClient();
  const [saving, setSaving] = useState(false);
  // Count of items that couldn't be saved on the last attempt (0 = none).
  const [failed, setFailed] = useState(0);
  const [failureReasons, setFailureReasons] = useState<string[]>([]);
  // True when the last Save was blocked because a date is invalid/out of range.
  const [dateBlocked, setDateBlocked] = useState(false);
  // True when the last Save was blocked because a field failed L2 validation (length/email/phone).
  const [fieldBlocked, setFieldBlocked] = useState(false);
  // True when the last Save was blocked because some field is still unreviewed.
  const [reviewBlocked, setReviewBlocked] = useState(false);
  // True when the last Save was blocked because a taken language is not a real language.
  const [languageBlocked, setLanguageBlocked] = useState(false);

  const items = actionableItems(preview);
  if (items.length === 0 || !preview) return null;
  const keys = items.map((i) => i.key);
  const progress = guideProgress(preview, choices);
  const open = progress.total - progress.done;
  // Prev/Next walk only the fields still LEFT TO REVIEW (the bar owns navigation;
  // the active card owns the keep/use decision). Each button is enabled only
  // while an unreviewed step remains in its direction — so once everything is
  // reviewed, both are disabled. (Decided cards are revisited via their Review
  // button, not the nav.)
  const hasNav = guideSteps(preview).length > 0;
  const { prev: hasPrev, next: hasNext } = unreviewedNeighbors(preview, choices, guideKey);
  const prevDisabled = saving || !hasPrev;
  const nextDisabled = saving || !hasNext;
  // The blocked-save notices auto-clear once their condition is resolved.
  const showReviewBlocked = reviewBlocked && open > 0;
  const showDateBlocked = dateBlocked && hasInvalidDates(preview, choices, edits, rowEdits, rowExistingEdits);
  const showFieldBlocked =
    fieldBlocked && hasInvalidFields(preview, choices, edits, rowEdits, rowExistingEdits);
  const showLanguageBlocked =
    languageBlocked && hasUnknownLanguages(preview, choices, rowEdits, rowExistingEdits);

  const save = async () => {
    // Defensive: the Save button is disabled while `!personId`, so this never
    // fires in that state via the UI — kept as a guard, excluded from coverage.
    /* v8 ignore next */
    if (!personId) return;
    // Each attempt must report its OWN blocker. Clear every notice up front: a gate
    // returns early, so it would otherwise leave a previous attempt's flag standing —
    // and since the gates run in a different order than the bar renders them, a stale
    // one can outrank the live one and explain the wrong thing.
    setReviewBlocked(false);
    setLanguageBlocked(false);
    setDateBlocked(false);
    setFieldBlocked(false);
    // Every change must be explicitly reviewed (a per-card decision or a bulk
    // Keep-all/Use-all) — defaults don't count. Block Save until nothing is left
    // to review, taking the user to the next undecided field.
    const unreviewedKey = firstUnreviewedKey(preview, choices);
    if (unreviewedKey) {
      activateStep(unreviewedKey);
      setReviewBlocked(true);
      return;
    }
    // A language the import could not resolve to a real tag must not be taken over:
    // genesis stores languageTag verbatim, so it would persist as permanent junk.
    // Take the user to the row so its chooser + note are on screen.
    const unknownLanguageKey = firstUnknownLanguageKey(preview, choices, rowEdits, rowExistingEdits);
    if (unknownLanguageKey) {
      activateStep(unknownLanguageKey);
      setLanguageBlocked(true);
      return;
    }
    // A date entered as free text can be malformed or after today.
    // Block the save, reveal every date warning, and commit nothing until fixed.
    const badDateKey = firstInvalidDateKey(preview, choices, edits, rowEdits, rowExistingEdits);
    if (badDateKey) {
      // Activate the first offending field's card so its warning is on screen —
      // notably a dateOfBirth on the currently-hidden Details tab (the ProfilePage
      // tab-sync effect follows the active step).
      activateStep(badDateKey);
      revealDateErrorsNow();
      setDateBlocked(true);
      return;
    }
    // A committed value can also be over-cap or an invalid email/phone (L2, mirroring the genesis
    // L1 sanitizer). Block, reveal every warning, and commit nothing until fixed.
    const badFieldKey =
      firstInvalidFieldKey(preview, choices, edits) ??
      firstInvalidRowFieldKey(preview, choices, rowEdits, rowExistingEdits);
    if (badFieldKey) {
      activateStep(badFieldKey);
      revealDateErrorsNow();
      setFieldBlocked(true);
      return;
    }
    setSaving(true);
    setFailed(0);
    setFailureReasons([]);
    try {
      const { savedKeys, failedKeys, failureReasons } = await commitProfileMerge({
        client: apollo,
        saveFailedFallback: intl.formatMessage({
          id: "profile.merge.saveFailed",
          defaultMessage: "This change could not be saved.",
        }),
        personId,
        expertiseBase,
        preview,
        resolutions: resolutionsFor(preview, choices, edits, rowEdits, rowExistingEdits),
      });
      // Reconcile the review to what actually persisted FIRST, so a later refetch
      // failure can't resurrect already-saved rows (a retry would re-create them).
      if (failedKeys.length === 0) {
        reset();
      } else {
        // Prune the saved rows from the review so a retry re-sends only the
        // failures and never re-creates an already-created entry.
        dropResolved(savedKeys);
        setFailed(failedKeys.length);
        // The reason, not just a count: a validation rejection tells the user which value to change.
        setFailureReasons(failureReasons);
      }
      // Best-effort refresh of the profile; it must not undo the reconcile above.
      await apollo.refetchQueries({ include: "active" }).catch(() => {});
    } catch {
      // Whole-commit failure (e.g. network) — keep every selection for a retry.
      setFailed(items.length);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed bottom-4 sm:bottom-8 left-1/2 z-[var(--z-toast)] w-[42rem] max-w-[calc(100vw-2rem)] -translate-x-1/2"
      role="region"
      aria-label={intl.formatMessage({ id: "profile.merge.bar.aria", defaultMessage: "Import changes" })}
      data-testid="profile-merge-bar"
    >
      <div className="smycles-toast">
        <div className="min-w-0 flex-1">
          {showDateBlocked ? (
            // The invalid date(s) are flagged inline under their field(s); this
            // explains why the Save click didn't proceed.
            <span className="font-medium" role="alert">
              <FormattedMessage
                id="profile.merge.dateBlocked"
                defaultMessage="Some dates need fixing before saving."
              />
            </span>
          ) : showLanguageBlocked ? (
            // The row carries the chooser and names the unrecognised text; this
            // explains why the Save click didn't proceed.
            <span className="font-medium" role="alert" data-testid="profile-merge-language-blocked">
              <FormattedMessage
                id="profile.merge.languageBlocked"
                defaultMessage="Choose a real language for the imported entry, or skip it."
              />
            </span>
          ) : showFieldBlocked ? (
            // A field is over-length or an invalid email/phone; flagged inline under the field(s).
            <span className="font-medium" role="alert">
              <FormattedMessage
                id="profile.merge.fieldBlocked"
                defaultMessage="Some values need fixing before saving."
              />
            </span>
          ) : failed > 0 ? (
            // Error inherits the on-accent ink: the toast surface IS the signal
            // color, so a coloured error span would be invisible.
            <span className="font-medium" role="alert">
              <FormattedMessage
                id="profile.merge.savePartial"
                defaultMessage="{count, plural, one {# change couldn't be saved. Please try again.} other {# changes couldn't be saved. Please try again.}}"
                values={{ count: failed }}
              />
              {failureReasons.length > 0 ? (
                <span className="ml-1 font-normal" data-testid="profile-merge-failure-reasons">
                  {failureReasons.join(" ")}
                </span>
              ) : null}
            </span>
          ) : (
            <div className="flex flex-col items-start gap-1.5">
              <span data-testid="profile-merge-open">
                {open > 0 ? (
                  <FormattedMessage
                    id="profile.merge.bar.open"
                    defaultMessage="{count, plural, one {# field left to review} other {# fields left to review}}"
                    values={{ count: open }}
                  />
                ) : (
                  <FormattedMessage id="profile.merge.bar.allReviewed" defaultMessage="All changes reviewed" />
                )}
              </span>
              {hasNav ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => stepBy(-1)}
                    disabled={prevDisabled}
                    className="flex items-center gap-1"
                    data-testid="profile-merge-nav-prev"
                  >
                    <Chevron direction="back" />
                    <FormattedMessage id="profile.merge.bar.previous" defaultMessage="Previous" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => stepBy(1)}
                    disabled={nextDisabled}
                    className="flex items-center gap-1"
                    data-testid="profile-merge-nav-next"
                  >
                    <FormattedMessage id="profile.merge.bar.next" defaultMessage="Next" />
                    <Chevron direction="next" />
                  </Button>
                </div>
              ) : null}
              {showReviewBlocked ? (
                // Nav stays available so the user can reach the unreviewed
                // fields; this line explains why the Save click didn't proceed.
                <span className="font-medium" role="alert" data-testid="profile-merge-review-blocked">
                  <FormattedMessage
                    id="profile.merge.reviewBlocked"
                    defaultMessage="Review every change before saving."
                  />
                </span>
              ) : null}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setMany(keys, "KEEP_EXISTING")} disabled={saving}>
            <FormattedMessage id="profile.merge.keepAll" defaultMessage="Keep all mine" />
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setMany(keys, "USE_INCOMING")} disabled={saving}>
            <FormattedMessage id="profile.merge.useAll" defaultMessage="Use all imported" />
          </Button>
          <Button variant="primary" size="sm" onClick={() => void save()} disabled={saving || !personId}>
            {saving ? (
              <FormattedMessage id="profile.merge.saving" defaultMessage="Saving…" />
            ) : (
              <FormattedMessage id="profile.merge.save" defaultMessage="Save changes" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
